import { validateIDNumber } from '@bihu/common-js'
import { Toast } from 'antd-mobile'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormSkinOption, FormSkinViewModel } from '../../types'

interface Options {

  /** 旧业务容器注入的当前展示数据；本 Hook 不读写业务 Store。 */
  model: FormSkinViewModel

  /** 本地身份校验通过后的旧提交动作。 */
  onSubmit: () => void

  /** 未通过本地身份校验时由旧容器保留原有 Toast 提示。 */
  onInvalidIdentity: () => void

  /** 用户离开页面的真实返回回调。 */
  onBack: () => void
}

export interface FieldErrorInfo {

  /** 浮层错误提示，为空时隐去浮层。 */
  errorTip: string | null

  /** 输入框 Placeholder 展示文案。 */
  placeholder: string
}

export interface FormSkinController {

  /** 资金需求编辑区是否展开，仅影响皮肤内展示。 */
  limitEditorOpen: boolean

  /** 挽留弹窗是否展示，仅影响皮肤内展示。 */
  retentionVisible: boolean

  /** 挽留弹窗关闭 ✕ 按钮是否延迟显示。 */
  retentionCanClose: boolean

  /** 真实姓名的本地输入错误状态。 */
  realNameInvalid: boolean

  /** 身份证号的本地输入错误状态。 */
  identityInvalid: boolean

  /** 真实姓名的本地校验提示与占位信息。 */
  realNameErrorInfo: FieldErrorInfo

  /** 身份证号的本地校验提示与占位信息。 */
  identityErrorInfo: FieldErrorInfo

  /** 当前资金需求的金钱格式化展示文本。 */
  applyLimitDisplay: string

  /** 用户点击 Header 返回按钮时调用，处理拦截或直接退出。 */
  onBack: () => void

  /** 用户关闭挽留弹窗时调用。 */
  onCloseRetention: () => void

  /** 用户在挽留弹窗确认放弃插队离开页面时调用。 */
  onConfirmLeave: () => void

  /** 用户在挽留弹窗中点击“立即提交”时调用。 */
  onSubmitFromRetention: () => void

  /** 用户切换资金需求编辑区时调用。 */
  onToggleLimitEditor: () => void

  /** 用户输入真实姓名后更新本地错误展示。 */
  onValidateRealName: (value: string) => void

  /** 用户输入身份证号后更新本地错误展示。 */
  onValidateIdentity: (value: string) => void

  /** 用户点击提交时执行本地校验。 */
  onSubmit: () => void
}

const RETENTION_CLOSE_DELAY = 1500
const RETENTION_TIMER_DELAY = 10000

const APPLY_LIMIT_DISPLAY: Record<string, string> = {
  4: '100,000～200,000',
  3: '50,000～100,000',
  2: '30,000～50,000',
  1: '0～30,000',
}

const getOptionLabel = (options: readonly FormSkinOption[], value: string): string => (
  options.find(option => option.value === value)?.label || ''
)

const FIELD_VALIDATION_RULES = {
  realName: {
    requiredMessage: '请填写您的真实姓名',
    invalidMessage: '请输入您的真实姓名',
  },
  identity: {
    requiredMessage: '请填写您的身份证号',
    invalidMessage: '请输入您的真实身份证号',
  },
} as const

const getFieldErrorInfo = (
  fieldName: 'realName' | 'identity',
  value: string,
  hasError: boolean,
): FieldErrorInfo => {
  if (!hasError) {
    return {
      errorTip: null,
      placeholder: '请输入',
    }
  }
  const rule = FIELD_VALIDATION_RULES[fieldName]
  const isEmpty = !value.trim()
  return {
    errorTip: isEmpty ? null : rule.invalidMessage,
    placeholder: isEmpty ? rule.requiredMessage : '请输入',
  }
}

const isRealNameValid = (value: string): boolean => /^[\u4e00-\u9fa5]+$/.test(value.trim())

const isIdentityValid = (value: string): boolean => validateIDNumber(value.trim())

/**
 * useFormSkinController — 管理表单皮肤的展开、输入错误与挽留状态。
 * @returns {FormSkinController} 仅供皮肤 Container 和 View 使用的本地交互接口
 */
const useFormSkinController = ({ model, onBack, onInvalidIdentity, onSubmit }: Options): FormSkinController => {
  const [limitEditorOpen, setLimitEditorOpen] = useState(false)
  const [retentionVisible, setRetentionVisible] = useState(false)
  const [retentionCanClose, setRetentionCanClose] = useState(false)
  const [realNameInvalid, setRealNameInvalid] = useState(false)
  const [identityInvalid, setIdentityInvalid] = useState(false)

  const identityValid = useMemo(() => (
    isRealNameValid(model.realName) && isIdentityValid(model.identity)
  ), [model.identity, model.realName])

  const retentionTimerEnabled = identityValid && Boolean(model.cityName) && !model.submitting

  const realNameErrorInfo = useMemo(() => (
    getFieldErrorInfo('realName', model.realName, realNameInvalid)
  ), [model.realName, realNameInvalid])

  const identityErrorInfo = useMemo(() => (
    getFieldErrorInfo('identity', model.identity, identityInvalid)
  ), [model.identity, identityInvalid])

  const applyLimitDisplay = useMemo(() => (
    APPLY_LIMIT_DISPLAY[model.applyLimit] || getOptionLabel(model.applyLimitOptions, model.applyLimit)
  ), [model.applyLimit, model.applyLimitOptions])

  const timedRetentionShownRef = useRef(false)
  const exitRetentionShownRef = useRef(false)
  const triggeredByExitRef = useRef(false)

  const triggerRetention = useCallback((byExit: boolean) => {
    const shownRef = byExit ? exitRetentionShownRef : timedRetentionShownRef
    if (shownRef.current) {
      return false
    }
    shownRef.current = true
    triggeredByExitRef.current = byExit
    // 两类挽留独立计数，第二次弹出时仍需遵守关闭按钮的展示延迟。
    setRetentionCanClose(false)
    setRetentionVisible(true)
    window.setTimeout(() => {
      setRetentionCanClose(true)
    }, RETENTION_CLOSE_DELAY)
    return true
  }, [])

  // 资料完整后才开始计时；任一表单值变化都会重新计时，避免用户仍在填写时被打断。
  useEffect(() => {
    if (!retentionTimerEnabled || timedRetentionShownRef.current) {
      return undefined
    }

    const timer = window.setTimeout(() => {
      triggerRetention(false)
    }, RETENTION_TIMER_DELAY)

    return () => window.clearTimeout(timer)
  }, [
    model.applyLimit,
    model.carProperty,
    model.cityName,
    model.houseProperty,
    model.housingFund,
    model.identity,
    model.job,
    model.realName,
    model.sesameCredit,
    model.socialSecurity,
    retentionTimerEnabled,
    triggerRetention,
  ])

  const handleBack = useCallback(() => {
    if (triggerRetention(true)) {
      return
    }
    onBack()
  }, [onBack, triggerRetention])

  const handleConfirmLeave = useCallback(() => {
    setRetentionVisible(false)
    if (triggeredByExitRef.current) {
      onBack()
    }
  }, [onBack])

  const handleSubmit = useCallback(() => {
    if (!identityValid) {
      setRealNameInvalid(!isRealNameValid(model.realName))
      setIdentityInvalid(!isIdentityValid(model.identity))
      onInvalidIdentity()
      return
    }

    if (!model.cityName) {
      Toast.show('请选择申请城市')
      return
    }

    onSubmit()
  }, [identityValid, model.cityName, model.identity, model.realName, onInvalidIdentity, onSubmit])

  const handleSubmitFromRetention = useCallback(() => {
    setRetentionVisible(false)
    handleSubmit()
  }, [handleSubmit])

  return {
    limitEditorOpen,
    retentionVisible,
    retentionCanClose,
    realNameInvalid,
    identityInvalid,
    realNameErrorInfo,
    identityErrorInfo,
    applyLimitDisplay,
    onBack: handleBack,
    onCloseRetention: () => setRetentionVisible(false),
    onConfirmLeave: handleConfirmLeave,
    onSubmitFromRetention: handleSubmitFromRetention,
    onToggleLimitEditor: () => setLimitEditorOpen(current => !current),
    onValidateRealName: value => setRealNameInvalid(!isRealNameValid(value)),
    onValidateIdentity: value => setIdentityInvalid(!isIdentityValid(value)),
    onSubmit: handleSubmit,
  }
}

export default useFormSkinController
